import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CareerPathNumerologyCalculator = () => {
  const [person, setPerson] = useState({
    name: '',
    birthDate: ''
  });
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleChange = (field, value) => {
    setPerson(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const calculateCareerPathNumber = () => {
    const validatePerson = (person) => {
      return person.name && person.birthDate;
    };

    if (!validatePerson(person)) {
      setError('Please enter all required information.');
      return;
    }

    const calculateLifePath = (birthDate) => {
      const birthDateNumbers = birthDate.split('-').join('');
      let lifePath = birthDateNumbers.split('').reduce((acc, num) => acc + parseInt(num), 0);
      while (lifePath > 9) {
        lifePath = lifePath.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
      }
      return lifePath;
    };

    const calculateSoulNumber = (name) => {
      const vowels = 'AEIOU';
      const nameNumbers = name.toUpperCase().split('').reduce((acc, letter) => {
        if (vowels.includes(letter)) {
          const letterValue = letter.charCodeAt(0) - 64;
          acc += letterValue > 0 && letterValue <= 26 ? letterValue : 0;
        }
        return acc;
      }, 0);
      
      let soulNumber = nameNumbers.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
      
      while (soulNumber > 9) {
        soulNumber = soulNumber.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
      }
      
      return soulNumber;
    };

    const lifePath = calculateLifePath(person.birthDate);
    const soulNumber = calculateSoulNumber(person.name);

    // Calculate Career Path Number
    const careerPathNumber = (lifePath + soulNumber) % 9 || 9;

    // Prepare chart data
    const careerChartData = {
      labels: ['Life Path', 'Soul Number', 'Career Path Number'],
      datasets: [
        {
          label: 'Numerology Values',
          data: [lifePath, soulNumber, careerPathNumber],
          fill: true,
          backgroundColor: 'rgba(79, 70, 229, 0.5)',
          borderColor: 'rgba(79, 70, 229, 1)',
          tension: 0.4
        }
      ]
    };

    setResults({
      name: person.name,
      birthDate: person.birthDate,
      lifePath,
      soulNumber,
      careerPathNumber
    });

    setChartData(careerChartData);
  };

  return (
    <div className="career-path-calculator max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-gray-100">
      <Helmet>
        <title>Career Path Numerology Calculator | Discover Your Career Potential</title>
        <meta name="description" content="Calculate your Career Path Number using numerology. Understand your career potential and opportunities." />
        <meta name="keywords" content="career path number, numerology calculator, career potential, career numerology" />
      </Helmet>

      <div className="calculator-header mb-12 text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Career Path Numerology Calculator</h1>
        <p className="text-lg text-purple-600 font-semibold">Discover Your Career Potential</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div>
          <h3 className="text-2xl font-semibold mb-4">Your Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                Name:
              </label>
              <input
                type="text"
                id="name"
                value={person.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded-lg py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-purple-500"
                placeholder="Enter your name"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="birthDate">
                Birth Date:
              </label>
              <input
                type="date"
                id="birthDate"
                value={person.birthDate}
                onChange={(e) => handleChange('birthDate', e.target.value)}
                className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded-lg py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>

        <button
          onClick={calculateCareerPathNumber}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 mt-8 transition duration-200"
        >
          Calculate Career Path Number
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Career Path Number Analysis</h3>
            <div className="space-y-12">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="font-medium mb-4">Your Details</h4>
                <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium text-purple-600">{results.name}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Birth Date:</span>
                    <span className="font-medium text-purple-600">{results.birthDate}</span>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                  <h5 className="font-medium mb-2">Life Path Number:</h5>
                  <p className="text-purple-600 text-2xl text-center">{results.lifePath}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                  <h5 className="font-medium mb-2">Soul Number:</h5>
                  <p className="text-purple-600 text-2xl text-center">{results.soulNumber}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                  <h5 className="font-medium mb-2">Career Path Number:</h5>
                  <p className="text-purple-600 text-2xl text-center">{results.careerPathNumber}</p>
                </div>
              </div>

              {/* Career Path Number Visualization */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Numerology Values</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Numerology Analysis',
                          color: '#4F46E5',
                          font: {
                            size: 16
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#4B5563',
                            font: {
                              size: 12
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            color: '#4B5563'
                          },
                          grid: {
                            color: '#E5E7EB'
                          }
                        },
                        x: {
                          ticks: {
                            color: '#4B5563'
                          },
                          grid: {
                            color: '#E5E7EB'
                          }
                        }
                      },
                      animation: {
                        duration: 1000,
                        easing: 'easeInOutQuart'
                      }
                    }}
                  />
                </div>
              </div>

              {/* Step-by-Step Explanation */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Step-by-Step Explanation</h4>
                <p className="text-gray-700 mb-2">
                  1. **Life Path Number**: This number is derived from your birth date and represents your life's journey and challenges.
                </p>
                <p className="text-gray-700 mb-2">
                  2. **Soul Number**: This number is calculated from the vowels in your name and reflects your inner self and desires.
                </p>
                <p className="text-gray-700 mb-2">
                  3. **Career Path Number**: This number is calculated by adding your Life Path Number and Soul Number, then reducing it to a single digit (1-9). It indicates your career potential and opportunities.
                </p>
              </div>

              {/* How to Use Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">How to Use</h4>
                <p className="text-gray-700 mb-2">
                  1. **Enter Your Information**: Fill in your name and birth date in the provided fields.
                </p>
                <p className="text-gray-700 mb-2">
                  2. **Calculate**: Click the "Calculate Career Path Number" button to generate your results.
                </p>
                <p className="text-gray-700 mb-2">
                  3. **Review Results**: Analyze your Career Path Number, Life Path Number, and Soul Number along with the visual representation of your numerology values.
                </p>
              </div>

              {/* FAQ Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">FAQ</h4>
                <div className="space-y-4">
                  <div>
                    <h5 className="font-semibold">What is a Career Path Number?</h5>
                    <p className="text-gray-700">The Career Path Number is a numerological calculation that indicates your career potential and opportunities based on your name and birth date.</p>
                  </div>
                  <div>
                    <h5 className="font-semibold">How is the Career Path Number calculated?</h5>
                    <p className="text-gray-700">It is calculated by adding your Life Path Number and Soul Number, then reducing the sum to a single digit (1-9).</p>
                  </div>
                  <div>
                    <h5 className="font-semibold">Can I use this calculator for anyone?</h5>
                    <p className="text-gray-700">Yes, you can use this calculator for anyone by entering their name and birth date.</p>
                  </div>
                </div>
              </div>

              {/* Features & Benefits Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Features & Benefits</h4>
                <div className="space-y-4">
                  <div>
                    <h5 className="font-semibold">Features:</h5>
                    <ul className="list-disc pl-5">
                      <li>Calculate your Career Path Number based on numerology.</li>
                      <li>Visual representation of your numerology values.</li>
                      <li>Step-by-step explanation of results.</li>
                      <li>Easy-to-use interface for quick calculations.</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold">Benefits:</h5>
                    <ul className="list-disc pl-5">
                      <li>Gain insights into your career potential.</li>
                      <li>Understand the influence of your name and birth date on your career.</li>
                      <li>Make informed decisions based on your Career Path Number.</li>
                      <li>Enhance your career awareness and planning.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default CareerPathNumerologyCalculator;